import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { view, site, dialogs, i18next, clipboard, hdrm, template } from '@yola/ws-sdk';
import { AddContentTrigger } from '@yola/ws-ui';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import contextMenu from 'src/js/modules/context-menu';
import editor from 'src/js/modules/editor';
import topbar from 'src/js/modules/topbar';
import trackEvent from 'src/js/modules/analytics/segment/trackers/track-event';
import constants from 'src/js/modules/analytics/segment/constants';
import commonConstants from 'src/js/modules/common/constants';
import getEmptyPageTargetElementAndPosition from '../helpers/get-empty-page-target-element-and-position';
import { COPY_BLOCK, CUT_BLOCK, PASTE_BLOCK } from '../constants/context-menu-block-items';
import { CONTEXT_MENU_EMPTY_PAGE_GROUP_NAME } from '../constants/common';
import getBlockDataFromClipboard from '../helpers/get-block-data-from-clipboard';
import getBlockFromClipboard from '../helpers/get-block-from-clipboard';

const { RIGHT_CLICK_TRIGGER_ID } = commonConstants.common;
const { updateStrategies } = hdrm.constants;

const restrictedBlocksToPastSelector = 'ws-block[id*="header"], ws-block[id*="footer"]';

const getContextMenuItems = (targetElement, adjacentPosition) => ({
  groupName: CONTEXT_MENU_EMPTY_PAGE_GROUP_NAME,
  element: targetElement,
  onMouseEnter: (e) => e.preventDefaultHandler(),
  onBeforeItemClick: (e) => {
    e.preventDefaultHandler();

    trackEvent(constants.events.CONTEXT_MENU_EMPTY_PAGE_CLICKED);
  },
  items: [
    {
      id: COPY_BLOCK,
      caption: i18next.t('Copy block'),
      disabled: () => true,
    },
    {
      id: CUT_BLOCK,
      caption: i18next.t('Cut block'),
      disabled: () => true,
    },
    {
      id: PASTE_BLOCK,
      caption: i18next.t('Paste block'),
      disabled: async () => {
        const data = await getBlockDataFromClipboard();

        if (data) {
          return data.blockElement.matches(restrictedBlocksToPastSelector);
        }

        if (clipboard.verifiers.hasClipboardElementData()) {
          const { element } = clipboard.accessors.getClipboardData();
          return element.matches(restrictedBlocksToPastSelector);
        }

        return true;
      },
      async onClick() {
        contextMenu.operations.hideContextMenu();
        const blockElement = await getBlockFromClipboard();
        const data = await getBlockDataFromClipboard();

        if (blockElement) {
          clipboard.operations.setClipboardData(blockElement);
        }

        const targetElementId = view.accessors.getLiveElementId(targetElement);
        const options = {
          strategy: updateStrategies.UPDATE_STATIC_ONLY,
          customColorsData: data?.parsedData?.customColors,
        };
        view.operations.pasteElement(targetElementId, adjacentPosition, options);
      },
    },
  ],
});

function EmptyPageTriggerContainer() {
  const dispatch = useDispatch();
  const [isEmpty, setIsEmpty] = useState(false);

  const emptyPageIgnoredBlocks = useSelector(template.selectors.getEmptyPageIgnoredBlocks);
  const isLoaded = useSelector(editor.selectors.getLoadedStatus);
  const usedBlocks = useSelector((state) => {
    const activePageId = site.selectors.getActivePageId(state);

    return site.selectors.getUsedBlocks(state, activePageId);
  });
  const topbarHeight = useSelector(topbar.selectors.getHeight);

  useEffect(() => {
    if (!isLoaded) return;

    if (usedBlocks.length) {
      setIsEmpty(!usedBlocks.some((blockId) => !emptyPageIgnoredBlocks.includes(blockId)));
    } else {
      setIsEmpty(view.verifiers.isEmpty());
    }
    // `usedBlocks` and `emptyPageIgnoredBlocks` props trigger hook with
    // old `isLoaded` prop state which leads to the early check of blocks
    // on page and as result to early `AddContentTrigger` hiding.
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, [isLoaded]);

  if (!isEmpty) return null;

  const { targetElement, adjacentPosition } = getEmptyPageTargetElementAndPosition();
  const targetElementId = view.accessors.getLiveElementId(targetElement);
  const onContextMenu = (offsetX, offsetY) => {
    const position = { x: offsetX, y: offsetY - topbarHeight };
    const groups = [getContextMenuItems(targetElement, adjacentPosition)];
    contextMenu.operations.showContextMenu({ position, groups, triggerId: RIGHT_CLICK_TRIGGER_ID });

    trackEvent(constants.events.CONTEXT_MENU_EMPTY_PAGE_DISPLAYED);
  };

  const onClick = () => {
    dispatch(
      dialogs.actions.show(dialogTypes.ADD_BLOCK, {
        targetElementId,
        adjacentPosition,
      })
    );
  };

  return (
    <AddContentTrigger
      onClick={onClick}
      onContextMenu={onContextMenu}
      centered
      captions={{
        title: i18next.t('Start building your page'),
        annotation: i18next.t('Add a block'),
      }}
    />
  );
}

export default EmptyPageTriggerContainer;
